import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Apple, AppWindow, Cloud, Database, Flame, KeyRound, Play, Plus, Smartphone, Triangle, Zap } from 'lucide-react'
import type { AppManifest, Connection, ConnectionCreate, ConnectionProvider, Integrations } from '@one-deployment/api'
import { Button, Dialog, Input, Label, SkeletonRows, Textarea } from '@/components/ui'
import { Button as SButton } from '@/components/shadcn/button'
import { ErrorState } from '@/components/status'
import { ApiError, del, enc, get, post } from '@/lib/api'
import { cn } from '@/lib/cn'
import { dateTime } from '@/lib/format'
import { qk } from '@/lib/query'
import { useIntegrations } from '@/features/projects/Settings'

const GitHubLogo = ({ cls = 'size-4' }: { cls?: string }) => (
  <svg viewBox="0 0 24 24" className={cls} fill="currentColor" aria-hidden>
    <path d="M12 .3a12 12 0 0 0-3.8 23.4c.6.1.8-.3.8-.6v-2.2c-3.3.7-4-1.4-4-1.4-.6-1.4-1.4-1.8-1.4-1.8-1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1 1.8 2.8 1.3 3.5 1 0-.8.4-1.3.7-1.6-2.7-.3-5.5-1.3-5.5-6 0-1.2.5-2.3 1.3-3.1-.2-.4-.6-1.6 0-3.2 0 0 1-.3 3.4 1.2a11.5 11.5 0 0 1 6 0c2.3-1.5 3.3-1.2 3.3-1.2.6 1.6.2 2.8.1 3.2.8.8 1.3 1.9 1.3 3.2 0 4.6-2.8 5.6-5.5 5.9.5.4.9 1.1.9 2.2v3.3c0 .3.1.7.8.6A12 12 0 0 0 12 .3" />
  </svg>
)

const LinearLogo = ({ cls = 'size-4' }: { cls?: string }) => (
  <svg viewBox="0 0 100 100" className={cls} fill="currentColor" aria-hidden>
    <path d="M1.2 61.5c-.2-1 1-1.6 1.7-.9l36.5 36.5c.7.7.1 1.9-.9 1.7A50 50 0 0 1 1.2 61.5ZM0 46.9c0 .4.1.7.4 1l51.7 51.7c.3.3.6.4 1 .4 2.2-.1 4.3-.4 6.5-.8.8-.2 1.1-1.2.5-1.8L2.6 39.9c-.6-.6-1.6-.3-1.8.5-.4 2.1-.7 4.3-.8 6.5Zm4.2-17c-.2.4-.1.9.2 1.2l64.5 64.5c.3.3.8.4 1.2.2 1.6-.7 3.2-1.5 4.7-2.4.6-.4.7-1.2.2-1.7L8.3 25c-.5-.5-1.3-.4-1.7.2-.9 1.5-1.7 3.1-2.4 4.7Zm8.4-11.6c-.4-.4-.4-1 0-1.4A50 50 0 0 1 83 87.4c-.4.4-1 .4-1.4 0L12.6 18.3Z" />
  </svg>
)

const SlackLogo = ({ cls = 'size-4' }: { cls?: string }) => (
  <svg viewBox="0 0 24 24" className={cls} fill="currentColor" aria-hidden>
    <path d="M5 15a2 2 0 1 1-2-2h2v2Zm1 0a2 2 0 1 1 4 0v5a2 2 0 1 1-4 0v-5Zm3-10a2 2 0 1 1 2-2v2H9Zm0 1a2 2 0 1 1 0 4H4a2 2 0 1 1 0-4h5Zm10 3a2 2 0 1 1 2 2h-2V9Zm-1 0a2 2 0 1 1-4 0V4a2 2 0 1 1 4 0v5Zm-3 10a2 2 0 1 1-2 2v-2h2Zm0-1a2 2 0 1 1 0-4h5a2 2 0 1 1 0 4h-5Z" />
  </svg>
)


type Health = 'ok' | 'warn' | 'err' | 'off'
const DOT: Record<Health, string> = { ok: 'bg-ok', warn: 'bg-warn', err: 'bg-err', off: 'bg-fg-faint' }

/** Services connected with a pasted key, and where to find that key. */
const KEYED: Record<Exclude<ConnectionProvider, 'github' | 'slack'>, { name: string; icon: (c: string) => React.ReactNode; how: string }> = {
  cloudflare: { name: 'Cloudflare', icon: (c) => <Cloud className={c} />, how: 'dash.cloudflare.com → My Profile → API Tokens → Create Token.' },
  vercel: { name: 'Vercel', icon: (c) => <Triangle className={c} />, how: 'vercel.com → Account Settings → Tokens. Pick the team as the scope.' },
  supabase: { name: 'Supabase', icon: (c) => <Database className={c} />, how: 'supabase.com/dashboard/account/tokens → Generate new token.' },
  trigger: { name: 'Trigger.dev', icon: (c) => <Zap className={c} />, how: 'Your project → API Keys. The secret key starts with tr_prod_ or tr_dev_.' },
  expo: { name: 'Expo', icon: (c) => <Smartphone className={c} />, how: "expo.dev → Account settings → Access tokens. A robot user's token is best." },
  firebase: { name: 'Firebase', icon: (c) => <Flame className={c} />, how: 'Firebase console → Project settings → Service accounts → Generate new private key. Paste the whole JSON file.' },
  'google-play': { name: 'Google Play', icon: (c) => <Play className={c} />, how: 'A Google Cloud service account invited in Play Console → Users and permissions. Paste its whole JSON key file.' },
  'app-store': { name: 'App Store Connect', icon: (c) => <Apple className={c} />, how: 'App Store Connect → Users and Access → Integrations → Team keys. Use an Admin key so builds can be signed and submitted. Paste the .p8 file with its key id, the issuer id and your Team ID.' },
}

const GROUPS: { title: string; services: (keyof typeof KEYED)[] | 'core' }[] = [
  { title: 'Code and work', services: 'core' },
  { title: 'Hosting', services: ['cloudflare', 'vercel', 'supabase', 'trigger'] },
  { title: 'Mobile', services: ['expo', 'firebase', 'google-play', 'app-store'] },
]

/**
 * Everything the organisation is connected to, one row per service: a tile per
 * App, account or key with a green or red dot, and a + to add another. The
 * details sit behind the tile, not on the page.
 */
export function ConnectionsPage() {
  const ints = useIntegrations()
  const d = ints.data
  return (
    <div className="h-full overflow-auto">
      <div className="flex flex-col gap-8 px-8 py-6">
        <div>
          <h1 className="text-lg font-semibold">Connections</h1>
          <p className="text-sm text-fg-subtle">Connected once for the whole organisation. Each project picks its team, repo and channel in its own settings.</p>
        </div>
        {ints.error ? <ErrorState error={ints.error} retry={() => void ints.refetch()} /> : !d ? <SkeletonRows rows={6} /> : GROUPS.map((g) => (
          <section key={g.title} className="flex flex-col gap-3">
            <h2 className="text-xs text-fg-subtle">{g.title}</h2>
            <div className="flex flex-col rounded-lg border border-border bg-surface-2">
              {g.services === 'core' ? <><GitHubRow d={d} /><LinearRow d={d} /><SlackRow d={d} /></> : g.services.map((p) => <KeyedRow key={p} provider={p} d={d} />)}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}

function ServiceRow({ logo, name, children, onAdd, addLabel }: { logo: React.ReactNode; name: string; children: React.ReactNode; onAdd: () => void; addLabel: string }) {
  return (
    <div className="flex items-center gap-5 px-5 py-4">
      <div className="flex w-44 shrink-0 items-center gap-2.5 font-medium">{logo}<span>{name}</span></div>
      <div className="flex min-w-0 flex-1 flex-wrap gap-3">{children}</div>
      <SButton variant="outline" size="icon-xs" title={addLabel} aria-label={addLabel} onClick={onAdd}
        className="size-7 shrink-0 rounded-full border-border-strong bg-surface-2 text-fg-muted hover:bg-hover hover:text-fg">
        <Plus size={14} />
      </SButton>
    </div>
  )
}

function Tile({ icon, name, kind, health, onClick }: { icon: React.ReactNode; name: string; kind: string; health: Health; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className="relative flex w-32 flex-col items-center gap-1.5 rounded-lg border border-border bg-surface-2 px-3 pt-4 pb-3 text-center transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 hover:border-border-strong hover:bg-hover">
      <span className={cn('absolute top-2 right-2 size-2 rounded-full', DOT[health])} />
      <span className="flex size-9 items-center justify-center rounded-md bg-surface-3 text-fg">{icon}</span>
      <span className="w-full truncate text-xs font-medium">{name}</span>
      <span className="w-full truncate text-2xs text-fg-faint">{kind}</span>
    </button>
  )
}

const Empty = ({ children }: { children: React.ReactNode }) => <span className="flex items-center gap-2 self-center text-xs text-fg-faint"><span className="size-2 rounded-full bg-err/70" />{children}</span>

function useRefresh() {
  const qc = useQueryClient()
  return () => void qc.invalidateQueries({ queryKey: qk.integrations() })
}

function openInstall(kind: 'github' | 'linear') {
  void get<{ url: string }>(`/api/integrations/${kind}/install`).then((r) => window.open(r.url, '_blank', 'noopener'))
}

// ── GitHub ────────────────────────────────────────────────────────────────

type GhOpen = { kind: 'app' } | { kind: 'project-token'; project: string } | { kind: 'conn'; c: Connection } | { kind: 'add' } | null

function GitHubRow({ d }: { d: Integrations }) {
  const g = d.github
  const [open, setOpen] = useState<GhOpen>(null)
  const extra = d.connections.filter((c) => c.provider === 'github')
  const none = !g.appReady && d.githubTokens.length === 0 && extra.length === 0
  const token = open?.kind === 'project-token' ? d.githubTokens.find((t) => t.project === open.project) : undefined
  return (
    <ServiceRow logo={<GitHubLogo cls="size-5" />} name="GitHub" onAdd={() => setOpen({ kind: 'add' })} addLabel="Add a GitHub App install or token">
      {none && <Empty>Not connected</Empty>}
      {g.appReady && (g.installations.length === 0
        ? <Tile icon={<AppWindow size={18} />} name={g.appSlug ?? 'GitHub App'} kind="App · not installed" health="warn" onClick={() => setOpen({ kind: 'app' })} />
        : g.installations.map((i) => <Tile key={i.id} icon={<AppWindow size={18} />} name={i.account} kind="App" health={i.suspended ? 'err' : 'ok'} onClick={() => setOpen({ kind: 'app' })} />))}
      {d.githubTokens.map((t) => <Tile key={t.project} icon={<KeyRound size={18} />} name={t.project} kind="Project token" health={t.last4 ? 'ok' : 'err'} onClick={() => setOpen({ kind: 'project-token', project: t.project })} />)}
      {extra.map((c) => <ConnTile key={c.id} c={c} icon={<KeyRound size={18} />} onClick={() => setOpen({ kind: 'conn', c })} />)}

      <Dialog open={open?.kind === 'app'} onOpenChange={(o) => !o && setOpen(null)} title="GitHub App" width={560}><AppDetails d={d} /></Dialog>
      <Dialog open={!!token} onOpenChange={(o) => !o && setOpen(null)} title="Project token">{token && <ProjectToken t={token} />}</Dialog>
      {open?.kind === 'conn' && <ConnDetails c={open.c} onClose={() => setOpen(null)} />}
      <Dialog open={open?.kind === 'add'} onOpenChange={(o) => !o && setOpen(null)} title="Add to GitHub">
        <div className="flex flex-col gap-3">
          {!g.appReady ? (
            <Choice icon={<AppWindow size={16} />} title="Create the GitHub App" body="One App for the organisation, with short-lived keys per repo. Recommended."><CreateGitHubApp /></Choice>
          ) : (
            <Choice icon={<AppWindow size={16} />} title="Install the App on another account" body="Or grant it more repos."><div><Button onClick={() => openInstall('github')}>Open GitHub</Button></div></Choice>
          )}
          <Choice icon={<KeyRound size={16} />} title="Add a token" body="A personal access token, checked with GitHub before it is saved."><AddKey provider="github" onDone={() => setOpen(null)} /></Choice>
        </div>
      </Dialog>
    </ServiceRow>
  )
}

function Choice({ icon, title, body, children }: { icon: React.ReactNode; title: string; body: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-3">
      <div className="flex items-center gap-2 text-sm font-medium">{icon}{title}</div>
      <p className="text-xs text-fg-subtle">{body}</p>
      {children}
    </div>
  )
}

function AppDetails({ d }: { d: Integrations }) {
  const g = d.github
  const repos = g.repos.filter((r) => r.installationId)
  const used = repos.filter((r) => r.linkedProject)
  return (
    <div className="flex flex-col gap-4 text-sm">
      <p className="text-fg-subtle">App <span className="font-mono">{g.appSlug}</span> · {g.installations.length ? g.installations.map((i) => i.account).join(', ') : 'not installed yet'} · reaches {repos.length} repo{repos.length === 1 ? '' : 's'}</p>
      {g.installations.length === 0 && <SyncInstalls />}
      <div>
        <div className="mb-1.5 text-xs text-fg-subtle">Used by projects</div>
        {used.length === 0 ? <p className="text-xs text-fg-faint">None yet.</p> : (
          <ul className="flex flex-col gap-1">{used.map((r) => (
            <li key={r.id} className="flex items-center gap-2 text-xs"><span className="font-mono">{r.owner}/{r.name}</span><Link to="/p/$project" params={{ project: r.linkedProject! }} className="ml-auto text-accent hover:underline">{r.linkedProject}</Link></li>
          ))}</ul>
        )}
      </div>
      <div><Button onClick={() => openInstall('github')}>Grant more repos</Button></div>
    </div>
  )
}

function ProjectToken({ t }: { t: Integrations['githubTokens'][number] }) {
  return (
    <div className="flex flex-col gap-3 text-sm">
      <p>Used by <b>{t.project}</b> for <span className="font-mono">{t.repo}</span>.</p>
      <p className="text-xs text-fg-subtle">{t.last4 ? `Ends …${t.last4}` : 'No token saved yet'}{t.updatedAt && ` · set ${dateTime(t.updatedAt)}`}</p>
      <div><Link to="/p/$project/settings/$section" params={{ project: t.project, section: 'other' }}><Button>Change in project settings</Button></Link></div>
    </div>
  )
}

/** Installed on GitHub but not shown here: ask GitHub directly, once on sight and on demand. */
function SyncInstalls() {
  const qc = useQueryClient()
  const m = useMutation({
    mutationFn: () => post<{ installations: number }>('/api/integrations/github/sync', {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.integrations() }),
  })
  const { mutate } = m
  useEffect(() => mutate(), [mutate])
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-fg-subtle">{m.isPending ? 'Checking GitHub…' : m.data ? (m.data.installations ? 'Found it.' : 'GitHub says it is not installed yet.') : 'Installed already?'}</span>
      <Button disabled={m.isPending} onClick={() => m.mutate()}>Check again</Button>
      {m.error && <span className="text-err">{m.error.message}</span>}
    </div>
  )
}


/**
 * GitHub's manifest flow wants a form POST, so build one and submit it: the
 * page goes to GitHub, the person presses Create, and GitHub comes back to the
 * Worker, which stores the key and sends them on to install the app.
 */
function CreateGitHubApp() {
  const m = useMutation({
    mutationFn: () => get<AppManifest>('/api/integrations/github/manifest'),
    onSuccess: (r) => {
      const f = document.createElement('form')
      f.method = 'post'
      f.action = r.url
      const i = document.createElement('input')
      i.type = 'hidden'
      i.name = 'manifest'
      i.value = r.manifest
      f.append(i)
      document.body.append(f)
      f.submit()
    },
  })
  return (
    <div className="flex items-center gap-3">
      <Button onClick={() => m.mutate()} disabled={m.isPending || m.isSuccess}>{m.isPending || m.isSuccess ? 'Opening GitHub…' : 'Create GitHub App'}</Button>
      {m.isError && <span className="text-xs text-err">{m.error.message}</span>}
    </div>
  )
}


// ── Linear ────────────────────────────────────────────────────────────────

function LinearRow({ d }: { d: Integrations }) {
  const [open, setOpen] = useState<string | null>(null)
  const orgs = d.linear.orgs
  const org = orgs.find((o) => o.id === open)
  return (
    <ServiceRow logo={<LinearLogo cls="size-5" />} name="Linear" onAdd={() => openInstall('linear')} addLabel="Connect a Linear workspace">
      {orgs.length === 0 && <Empty>Not connected</Empty>}
      {orgs.map((o) => <Tile key={o.id} icon={<LinearLogo cls="size-[18px]" />} name={o.name} kind={o.auth === 'failed' ? 'Reconnect' : o.guardTripped ? 'Writes stopped' : 'Workspace'} health={o.auth === 'failed' ? 'err' : o.guardTripped ? 'warn' : 'ok'} onClick={() => setOpen(o.id)} />)}
      <Dialog open={!!org} onOpenChange={(o) => !o && setOpen(null)} title={org?.name ?? 'Linear'}>{org && <LinearDetails o={org} />}</Dialog>
    </ServiceRow>
  )
}

function LinearDetails({ o }: { o: Integrations['linear']['orgs'][number] }) {
  const refresh = useRefresh()
  const reset = useMutation({ mutationFn: () => post(`/api/integrations/linear/${enc(o.id)}/guard/reset`, {}), onSuccess: refresh })
  return (
    <div className="flex flex-col gap-3 text-sm">
      <p className="text-fg-subtle">{o.teams.length} team{o.teams.length === 1 ? '' : 's'}: {o.teams.map((t) => t.key).join(', ') || 'none synced yet'}.{o.expiresAt && o.auth === 'ok' && <> Access renews itself; the current one runs until {dateTime(o.expiresAt)}.</>}</p>
      {o.auth === 'failed' && <p className="text-xs text-err">Linear refused our access and renewing it failed. Reconnect and approve; waiting writes then go out.</p>}
      {o.guardTripped && <p className="text-xs text-warn">The write guard stopped writes to Linear{o.guardReason && `: ${o.guardReason}`}. It trips when writes look wrong, because a rate-limit incident once reassigned 61 tickets.</p>}
      <div className="flex gap-2">
        {o.guardTripped && <Button variant="primary" disabled={reset.isPending} onClick={() => reset.mutate()}>Resume writes</Button>}
        <Button onClick={() => openInstall('linear')}>Reconnect</Button>
      </div>
      {reset.error && <ErrorState error={reset.error} compact />}
    </div>
  )
}

// ── Slack ─────────────────────────────────────────────────────────────────

function SlackRow({ d }: { d: Integrations }) {
  const s = d.slack
  const [open, setOpen] = useState<'bot' | 'add' | Connection | null>(null)
  const extra = d.connections.filter((c) => c.provider === 'slack')
  return (
    <ServiceRow logo={<SlackLogo cls="size-5" />} name="Slack" onAdd={() => setOpen('add')} addLabel="Add a Slack bot token">
      {!s.ready && extra.length === 0 && <Empty>Not connected</Empty>}
      {s.ready && <Tile icon={<SlackLogo cls="size-[18px]" />} name={s.team ?? 'Slack'} kind={s.bot ? `@${s.bot}` : 'Bot'} health={s.error ? 'err' : 'ok'} onClick={() => setOpen('bot')} />}
      {extra.map((c) => <ConnTile key={c.id} c={c} icon={<SlackLogo cls="size-[18px]" />} onClick={() => setOpen(c)} />)}
      <Dialog open={open === 'bot'} onOpenChange={(o) => !o && setOpen(null)} title="Slack bot">
        <div className="flex flex-col gap-2 text-sm">
          {s.error ? <p className="text-err">Slack said: {s.error}. Set a new bot token.</p> : <p>{s.team} · bot @{s.bot}</p>}
          <p className="text-xs text-fg-subtle">Each project with a channel gets a morning summary at 07:00 UTC and spike alerts. Set the channel in the project&apos;s settings and invite the bot to it.</p>
        </div>
      </Dialog>
      <Dialog open={open === 'add'} onOpenChange={(o) => !o && setOpen(null)} title="Add a Slack bot" description="A bot token from api.slack.com/apps → OAuth & Permissions. It starts with xoxb-.">
        <AddKey provider="slack" onDone={() => setOpen(null)} />
      </Dialog>
      {open && typeof open === 'object' && <ConnDetails c={open} onClose={() => setOpen(null)} />}
    </ServiceRow>
  )
}

// ── Pasted-key services ───────────────────────────────────────────────────

function KeyedRow({ provider, d }: { provider: keyof typeof KEYED; d: Integrations }) {
  const k = KEYED[provider]
  const list = d.connections.filter((c) => c.provider === provider)
  const [open, setOpen] = useState<'add' | Connection | null>(null)
  return (
    <ServiceRow logo={k.icon('size-5')} name={k.name} onAdd={() => setOpen('add')} addLabel={`Add a ${k.name} key`}>
      {list.length === 0 && <Empty>Not connected</Empty>}
      {list.map((c) => <ConnTile key={c.id} c={c} icon={k.icon('size-[18px]')} onClick={() => setOpen(c)} />)}
      <Dialog open={open === 'add'} onOpenChange={(o) => !o && setOpen(null)} title={`Connect ${k.name}`} description={k.how}>
        <AddKey provider={provider} onDone={() => setOpen(null)} />
      </Dialog>
      {open && typeof open === 'object' && <ConnDetails c={open} onClose={() => setOpen(null)} />}
    </ServiceRow>
  )
}

function ConnTile({ c, icon, onClick }: { c: Connection; icon: React.ReactNode; onClick: () => void }) {
  const kind = [c.isDefault && 'Default', c.usedBy.length > 0 && `${c.usedBy.length} project${c.usedBy.length === 1 ? '' : 's'}`].filter(Boolean).join(' · ') || (c.last4 ? `Key …${c.last4}` : 'Key file')
  return <Tile icon={icon} name={c.label} kind={kind} health={c.status === 'ok' ? 'ok' : 'err'} onClick={onClick} />
}

function AddKey({ provider, onDone }: { provider: ConnectionProvider; onDone: () => void }) {
  const refresh = useRefresh()
  const [secret, setSecret] = useState('')
  const [label, setLabel] = useState('')
  const [issuerId, setIssuerId] = useState('')
  const [keyId, setKeyId] = useState('')
  const [teamId, setTeamId] = useState('')
  const file = provider === 'firebase' || provider === 'google-play' || provider === 'app-store'
  const m = useMutation({
    mutationFn: () => post<Connection>('/api/integrations/connections', { provider, secret, ...(label.trim() ? { label: label.trim() } : {}), ...(provider === 'app-store' ? { issuerId, keyId, ...(teamId.trim() ? { teamId: teamId.trim() } : {}) } : {}) } satisfies ConnectionCreate),
    onSuccess: () => { refresh(); onDone() },
  })
  return (
    <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); m.mutate() }}>
      <div>
        <Label htmlFor={`k-${provider}`}>{provider === 'app-store' ? '.p8 key' : file ? 'Service account JSON' : 'Token'}</Label>
        {file
          ? <Textarea id={`k-${provider}`} rows={6} className="font-mono text-xs" value={secret} onChange={(e) => setSecret(e.target.value)} />
          : <Input id={`k-${provider}`} type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} />}
      </div>
      {provider === 'app-store' && (
        <div className="grid grid-cols-2 gap-3">
          <div><Label htmlFor="asc-issuer">Issuer id</Label><Input id="asc-issuer" value={issuerId} onChange={(e) => setIssuerId(e.target.value)} /></div>
          <div><Label htmlFor="asc-key">Key id</Label><Input id="asc-key" value={keyId} onChange={(e) => setKeyId(e.target.value)} /></div>
          <div className="col-span-2"><Label htmlFor="asc-team">Apple Team ID (developer.apple.com → Membership; lets Claude sign iOS builds)</Label><Input id="asc-team" value={teamId} onChange={(e) => setTeamId(e.target.value)} /></div>
        </div>
      )}
      <div><Label htmlFor={`l-${provider}`}>Name (optional)</Label><Input id={`l-${provider}`} placeholder="Taken from the account if empty" value={label} onChange={(e) => setLabel(e.target.value)} /></div>
      <p className="text-xs text-fg-faint">Checked with the service first, then stored encrypted. It is never shown again.</p>
      {m.error && <div role="alert" className="text-xs text-err">{m.error instanceof ApiError ? (m.error.what ?? m.error.message) : String(m.error)}</div>}
      <div><Button type="submit" variant="primary" disabled={m.isPending || secret.trim().length < 8}>{m.isPending ? 'Checking…' : 'Connect'}</Button></div>
    </form>
  )
}

function ConnDetails({ c, onClose }: { c: Connection; onClose: () => void }) {
  const refresh = useRefresh()
  const [sure, setSure] = useState(false)
  const check = useMutation({ mutationFn: () => post<Connection>(`/api/integrations/connections/${enc(c.id)}/check`, {}), onSuccess: refresh })
  const remove = useMutation({ mutationFn: () => del(`/api/integrations/connections/${enc(c.id)}`), onSuccess: () => { refresh(); onClose() } })
  const makeDefault = useMutation({ mutationFn: () => post(`/api/integrations/connections/${enc(c.id)}/default`, {}), onSuccess: () => { refresh(); onClose() } })
  const now = check.data ?? c
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={c.label}>
      <div className="flex flex-col gap-3 text-sm">
        <div className="flex items-center gap-2">
          <span className={cn('size-2 rounded-full', now.status === 'ok' ? 'bg-ok' : 'bg-err')} />
          <span>{now.status === 'ok' ? 'Connected' : 'Not working'}</span>
          {now.account && <span className="text-fg-subtle">· {now.account}</span>}
        </div>
        {now.error && <p className="text-xs text-err">{now.error}</p>}
        <p className="text-xs text-fg-faint">{c.last4 && `Key ends …${c.last4} · `}added {dateTime(c.createdAt)} · checked {dateTime(now.checkedAt)}</p>
        <div>
          <div className="mb-1 text-xs text-fg-subtle">Used by</div>
          {c.usedBy.length === 0 ? <p className="text-xs text-fg-faint">No project yet.{c.isDefault && ' New projects get it unless they pick another.'}</p> : (
            <div className="flex flex-wrap gap-1.5">{c.usedBy.map((k) => <Link key={k} to="/p/$project/settings/$section" params={{ project: k, section: 'general' }} className="rounded-full bg-surface-2 px-2 py-0.5 text-xs hover:bg-hover">{k}</Link>)}</div>
          )}
        </div>
        <div className="flex gap-2">
          <Button disabled={check.isPending} onClick={() => check.mutate()}>{check.isPending ? 'Checking…' : 'Check now'}</Button>
          {c.isDefault ? <span className="self-center text-xs text-fg-subtle">Default account</span> : <Button disabled={makeDefault.isPending} onClick={() => makeDefault.mutate()}>Make default</Button>}
          <Button variant="danger" className="ml-auto" disabled={remove.isPending} onClick={() => (sure ? remove.mutate() : setSure(true))}>{sure ? 'Click again to remove' : 'Remove'}</Button>
        </div>
        {(check.error || remove.error || makeDefault.error) && <ErrorState error={check.error ?? remove.error ?? makeDefault.error} compact />}
      </div>
    </Dialog>
  )
}
